import { useState } from 'react'
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@cutilei/ui'
import AuthService from 'services/auth'
import cutileiApi from 'services/cutileiApi'
import { logApiError } from 'services/apiError'
import MaskedInputService from 'services/maskedInput'
import Select from 'components/Inputs/Select'
import MainButton from 'components/Buttons/MainButton'
import submitOnEnter from 'utils/submitOnEnter'
import { Formik } from 'formik'
import { DateTime } from 'luxon'

import {
  Form,
  FormField,
  Input,
  Row,
  WarningText,
  ErrorContainer,
  MaskedInput
} from './styles'

export default function EditMemberInfoModal ({
  visible,
  data: member,
  onConfirm: handleConfirm,
  onClose: handleClose
}) {
  const [errorMessage, setErrorMessage] = useState (null)
  const token = AuthService.getToken ()

  const requestConfig = {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  }

  if (!member) return null

  return (
    <Dialog open={visible} onOpenChange={open => { if (!open) handleClose() }}>
      <DialogContent size='sm' closeOnOutsideClick={false} aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>Editar dados do membro</DialogTitle>
        </DialogHeader>
        <Formik
          initialValues={{
            'name': member.name,
            'email': member.email,
            'cpf': member.cpf,
            'phone': member.phone,
            'gender': {
              label: member.gender,
              value: member.gender
            },
            'birth': DateTime.fromISO (member.birth).toFormat ('ddMMyyyy')
          }}
          onSubmit={async (values, { setSubmitting }) => {
            try {
              setSubmitting (true)

              const { data: updatedMember } = await cutileiApi.put (`/members/${member.id}`, {
                ...values,
                gender: values.gender.value,
                birth: DateTime.fromFormat (values.birth, 'ddMMyyyy').toISODate ()
              }, requestConfig)

              setSubmitting (false)
              handleConfirm (updatedMember)
              handleClose ()
            } catch (error) {
              setSubmitting (false)
              logApiError (error)
              setErrorMessage (
                error.response?.data?.message ?? 'Erro ao editar dados do membro. Tente novamente.'
              )
            }
          }}
        >
          {function EditMemberInfoForm ({
            values,
            errors,
            touched,
            isSubmitting,
            setFieldValue,
            setFieldTouched,
            handleChange,
            handleBlur, 
            handleSubmit
          }) {
            return (
              <>
                <DialogBody>
                  <Form onKeyDown={submitOnEnter(handleSubmit, isSubmitting)}>
                    <Row>
                      <FormField stretch={2}>
                        <Input
                          placeholder='Nome completo'
                          value={values.name}
                          onChange={handleChange ('name')}
                          onBlur={handleBlur ('name')}
                        />
                        {touched.name && errors.name && (
                          <WarningText>
                            {errors.name}
                          </WarningText>
                        )}
                      </FormField>
                    </Row>
                    <Row>    
                      <FormField>
                        <Input
                          type='email'
                          placeholder='E-mail'
                          value={values.email}
                          onChange={handleChange ('email')}
                          onBlur={handleBlur ('email')}
                        />
                        {touched.email && errors.email && (
                          <WarningText>
                            {errors.email}
                          </WarningText>
                        )}
                      </FormField>
                    </Row>
                    <Row>
                      <FormField>
                        <MaskedInput
                          name='cpf'
                          placeholder='CPF'
                          options={{
                            blocks: [3,3,3,2],
                            delimiters: ['.', '.', '-'],
                            numericOnly: true
                          }}
                          value={values.cpf}
                          onChange={event => MaskedInputService.handleChange (event, setFieldValue)}
                          onBlur={handleBlur ('cpf')}
                        />
                        {touched.cpf && errors.cpf && (
                          <WarningText>
                            {errors.cpf}
                          </WarningText>
                        )}
                      </FormField>
                      <FormField>
                        <MaskedInput
                          name='phone'
                          placeholder='Celular'
                          options={{
                            blocks: [0, 2, 5, 4],
                            delimiters: ['(', ') ', '-'],
                            numericOnly: true,
                          }}
                          value={values.phone}
                          onChange={event => MaskedInputService.handleChange (event, setFieldValue)}
                          onBlur={handleBlur ('phone')}
                        />
                        {touched.phone && errors.phone && (
                          <WarningText>
                            {errors.phone}
                          </WarningText>
                        )}
                      </FormField>
                    </Row>
                    <Row>
                      <FormField>
                        <MaskedInput
                          name='birth'
                          placeholder='Nascimento'
                          options={{date: true, datePattern: ['d', 'm', 'Y']}}
                          value={values.birth}
                          onChange={event => MaskedInputService.handleChange (event, setFieldValue)}
                          onBlur={handleBlur ('birth')}
                        />
                        {touched.birth && errors.birth && (
                          <WarningText>
                            {errors.birth}
                          </WarningText>
                        )}
                      </FormField>
                      <FormField>
                        <Select
                          name='gender'
                          placeholder='Gênero...'
                          value={values.gender}
                          options={[
                            {label: 'Masculino', value: 'Masculino'},
                            {label: 'Feminino', value: 'Feminino'},
                            {label: 'Não informar', value: 'Não informar'}
                          ]}
                          onChange={setFieldValue}
                          onBlur={setFieldTouched}
                          error={errors.gender}
                          touched={touched.gender}
                        />
                        {touched.gender && errors.gender && (
                          <WarningText>
                            {errors.gender}
                          </WarningText>
                        )}
                      </FormField>
                    </Row>
                    {errorMessage && (
                      <ErrorContainer>
                        <p>{errorMessage}</p>
                      </ErrorContainer>
                    )}
                  </Form>
                </DialogBody>
                <DialogFooter>
                  <MainButton
                    label='Salvar alterações'
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    loading={isSubmitting}
                    width={160}
                  />
                </DialogFooter>
              </>
            )
          }}
        </Formik>
      </DialogContent>
    </Dialog>
  )
}
